import { useTranslation } from 'react-i18next'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import { useStoreDispatch } from 'hooks/store'
import { useGetLanguage } from 'hooks/use-get-language'
import styles from './Move.module.scss'
import { routes } from 'types/routes.ts'
import Link from 'components/link/Link.tsx'
import { IS_BETA } from 'constants/envs.ts'
import { FaqSlugB2C } from 'types/faq.ts'
import { Button, Card, StepList, VideoCard } from '@boltenergy-be/design-system'
import { trackEvent } from 'utils/tracking.ts'
import { MOVE_FLOWS } from '@boltenergy-be/shared'
import { ContractFlowStartPoints, ContractFlowTrackingTypes } from 'types/tracking.ts'
import { setMoveFlow } from 'store/app/slice.ts'
import { useNavigate } from 'react-router'
import { useCheckResubscriptionStatus } from 'hooks/useCheckResubscriptionStatus.ts'
import { useState } from 'react'
import BlockResubscriptionModal from 'components/block-resubscription-modal/BlockResubscriptionModal.tsx'

const Move = () => {
  // Redux
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['move', 'referral', 'common'])
  const language = useGetLanguage()
  const lowerCaseLanguage = useGetLanguage('lowercase')

  // Router
  const navigate = useNavigate()

  // Hooks
  const resubscriptionStatus = useCheckResubscriptionStatus()

  // Local state
  const [showResubscriptionModal, setShowResubscriptionModal] = useState<boolean>(false)

  /**
   * Start move flow and handle necessary tracking
   */
  const handleContinue = () => {
    trackEvent(
      { category: 'contract', object: `${ContractFlowTrackingTypes.ADD}_contract`, action: 'start' },
      { flow: MOVE_FLOWS.startStop, type: ContractFlowTrackingTypes.ADD, from: ContractFlowStartPoints.MOVE }
    )
    dispatch(setMoveFlow(MOVE_FLOWS.startStop))
    void navigate(routes.CONTRACTS_MOVE)
  }

  /**
   * Check resubscription status before continue
   */
  const handleStartMove = () => {
    if (resubscriptionStatus === 'block') {
      setShowResubscriptionModal(true)
    } else {
      handleContinue()
    }
  }

  return (
    <>
      <PageLayout
        title={t(($) => $.nav.move, {
          ns: 'common'
        })}
        faqCategories={{ b2c: [FaqSlugB2C.MOVE] }}
        description={t(($) => $.info.description)}
        banner={
          IS_BETA
            ? {
                type: 'informative',
                message: t(($) => $.betaBanner),
                className: 'mb-400'
              }
            : undefined
        }
      >
        <div className={styles.grid}>
          <Card className={styles.info}>
            <Card.Title as="h2" variant="h4" serif={false}>
              {t(($) => $.info.subtitle)}
            </Card.Title>

            <StepList size="medium" className={styles['step-list']}>
              <li>
                <div>
                  <strong>{t(($) => $.info.steps.newAddress.title)}</strong>
                  <p>{t(($) => $.info.steps.newAddress.description)}</p>
                </div>
              </li>
              <li>
                <div>
                  <strong>{t(($) => $.info.steps.oldAddress.title)}</strong>
                  <p>{t(($) => $.info.steps.oldAddress.description)}</p>
                </div>
              </li>
              <li>
                <div>
                  <strong>{t(($) => $.info.steps.referral.title)}</strong>
                  <p>{t(($) => $.info.steps.referral.description)}</p>
                </div>
              </li>
            </StepList>

            <p>{t(($) => $.info.steps.extra)}</p>

            {!IS_BETA && (
              <Button onClick={handleStartMove} loading={resubscriptionStatus === 'checking'}>
                {t(($) => $.info.steps.button)}
              </Button>
            )}
          </Card>

          <Card className={styles['video-wrapper']}>
            <VideoCard
              publicId={`website/videos/move-${language}`}
              alt=""
              thumbnailPublicId={`my-bolt/move/move-thumbnail-${lowerCaseLanguage}`}
              className={styles['thumbnail-wrapper']}
            />
            <figcaption>{t(($) => $.info.videoCaption)}</figcaption>
          </Card>

          <Card as="section" color="dark">
            <Card.Title as="h2" variant="h4">
              {t(($) => $.help.title)}
            </Card.Title>
            <p>{t(($) => $.help.description)}</p>
            <p>{t(($) => $.help.availability)}</p>

            <Link
              representation="button"
              href={routes.SUPPORT_CONTACT}
              variant="secondary"
              trailingIcon="chevronRight"
              isFullwidth
              className="mt-auto"
              onNavigate={() => trackEvent({ category: 'move', object: 'help', action: 'click' })}
            >
              {t(($) => $.help.action)}
            </Link>
          </Card>
        </div>
      </PageLayout>

      <BlockResubscriptionModal
        isOpen={showResubscriptionModal}
        onRequestClose={() => setShowResubscriptionModal(false)}
        onContinue={handleContinue}
      />
    </>
  )
}

export default Move
